import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, AlertTriangle, Wallet, Bitcoin, X, Star, Copy, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', color: '#ffffff', textAlign: 'center' }}>
          <h1 style={{ color: '#ef4444', marginBottom: '20px' }}>Something went wrong</h1>
          <p style={{ color: '#9ca3af' }}>{this.state.error?.toString()}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const Products = () => {
  const { user } = useAuth();

  // Helper function to format text with bullet points
  const formatTextWithBullets = (text) => {
    if (!text) return text;
    
    return text.split('\n').map((line, index) => {
      // Check if line starts with "- "
      if (line.trim().startsWith('- ')) {
        return (
          <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
            <span>•</span>
            <span>{line.trim().substring(2)}</span>
          </div>
        );
      }
      // Regular line
      return <div key={index} style={{ marginBottom: line.trim() ? '4px' : '0' }}>{line || '\u00A0'}</div>;
    });
  };

  // Helper function to copy text to clipboard
  const copyToClipboard = (text, fieldKey) => {
    navigator.clipboard.writeText(text).then(() => {
      if (fieldKey) {
        setCopiedFields(prev => ({ ...prev, [fieldKey]: true }));
        setTimeout(() => {
          setCopiedFields(prev => ({ ...prev, [fieldKey]: false }));
        }, 2000);
      }
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  // Helper function to copy all description content with titles
  const copyAllDescription = (descData) => {
    const sections = [];
    if (descData.requirements) sections.push(`Requirements\n${descData.requirements}`);
    if (descData.information) sections.push(`Information\n${descData.information}`);
    if (descData.aimbot) sections.push(`Aimbot\n${descData.aimbot}`);
    if (descData.visuals) sections.push(`Visuals\n${descData.visuals}`);
    if (descData.world) sections.push(`World\n${descData.world}`);
    if (descData.configs) sections.push(`Configs\n${descData.configs}`);
    if (descData.misc) sections.push(`Misc\n${descData.misc}`);
    
    const allText = sections.join('\n\n');
    copyToClipboard(allText, 'copyAll');
  };

  // Check if user has API key on mount
  useEffect(() => {
    const checkApiKey = async () => {
      try {
        const response = await fetch('/api/api-keys', {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        if (data.success && data.data.master_key) {
          setHasApiKey(true);
        } else {
          setHasApiKey(false);
        }
      } catch (error) {
        console.error('Failed to check API key:', error);
        setHasApiKey(false);
      } finally {
        setIsCheckingApiKey(false);
      }
    };
    
    if (user) {
      checkApiKey();
    }
  }, [user]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null); // null, 'balance', 'btc', 'ltc', 'method4'
  const [variantQuantities, setVariantQuantities] = useState({}); // { variantId: quantity }
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupPaymentMethod, setTopupPaymentMethod] = useState(null);
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
  const [insufficientBalanceData, setInsufficientBalanceData] = useState(null);
  const [showOutOfStockModal, setShowOutOfStockModal] = useState(false);
  const [outOfStockVariants, setOutOfStockVariants] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApiLinksModal, setShowApiLinksModal] = useState(false);
  const [apiLinks, setApiLinks] = useState(null);
  const [isLoadingApiLinks, setIsLoadingApiLinks] = useState(false);
  const [selectedProductForApiLinks, setSelectedProductForApiLinks] = useState(null);
  const [copiedVariantId, setCopiedVariantId] = useState(null);
  const [purchaseResult, setPurchaseResult] = useState(null); // { success: bool, data: object }
  const [hasApiKey, setHasApiKey] = useState(false);
  const [isCheckingApiKey, setIsCheckingApiKey] = useState(true);
  const [showApiLinksConfirmModal, setShowApiLinksConfirmModal] = useState(false);
  const [copiedFields, setCopiedFields] = useState({}); // Track which fields have been copied
  
  // Get user's discount from JWT token
  const getUserDiscount = () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) return 0;
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.discount_percentage || 0;
    } catch (error) {
      console.error('Error reading discount from token:', error);
      return 0;
    }
  };
  
  const userDiscountPercent = getUserDiscount();
  
  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const token = sessionStorage.getItem('token');
        // Fetch all products in display order
        const response = await fetch('/api/products?limit=100', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const data = await response.json();
        if (data.success && data.data && data.data.products) {
          // Map status values to display format
          const mappedProducts = data.data.products.map(product => ({
            ...product,
            status: product.status === 'undetected' ? 'Undetected' :
                   product.status === 'detected' ? 'Detected' :
                   product.status === 'use_at_own_risk' ? 'Use At Own Risk' :
                   product.status === 'updating' ? 'Updating' :
                   product.status === 'testing' ? 'Testing' : 'Undetected',
            variants: (product.variants || []).map(v => ({
              ...v,
              price: parseFloat(v.price)
            }))
          }));
          setProducts(mappedProducts);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownOpen && !event.target.closest('.category-dropdown-container')) {
        setCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categoryDropdownOpen]);

  // Get status color and icon
  const getStatusInfo = (status) => {
    if (status === 'Undetected') {
      return { color: '#22c55e', icon: CheckCircle }; // Green
    } else if (status === 'Detected') {
      return { color: '#ef4444', icon: AlertTriangle }; // Red
    } else if (status === 'Use At Own Risk') {
      return { color: '#eab308', icon: AlertTriangle }; // Yellow
    } else if (status === 'Updating' || status === 'Testing') {
      return { color: '#60a5fa', icon: CheckCircle }; // Light blue
    }
    return { color: '#22c55e', icon: CheckCircle };
  };

  // Get unique categories from products
  const categories = React.useMemo(() => {
    const uniqueCategories = [...new Set(products.map(p => p.category).filter(Boolean))];
    return uniqueCategories.sort();
  }, [products]);

  // Filter products based on search and category
  const filteredProducts = React.useMemo(() => {
    return products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !selectedCategory || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, selectedCategory]);

  // Selected product state
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Set first product as selected when filtered products load
  useEffect(() => {
    if (filteredProducts.length > 0 && !selectedProduct) {
      setSelectedProduct(filteredProducts[0]);
    }
  }, [filteredProducts, selectedProduct]);

  // Quantity handlers
  const incrementQuantity = (variantId) => {
    setVariantQuantities(prev => ({
      ...prev,
      [variantId]: (prev[variantId] || 0) + 1
    }));
  };

  const decrementQuantity = (variantId) => {
    setVariantQuantities(prev => ({
      ...prev,
      [variantId]: Math.max(0, (prev[variantId] || 0) - 1)
    }));
  };

  const handleQuantityChange = (variantId, value) => {
    const numValue = parseInt(value) || 0;
    setVariantQuantities(prev => ({
      ...prev,
      [variantId]: Math.max(0, numValue)
    }));
  };

  // Calculate subtotal using UNDISCOUNTED prices (original prices)
  const calculateSubtotal = () => {
    if (!selectedProduct || !selectedProduct.variants) return 0;
    return selectedProduct.variants.reduce((total, variant) => {
      const quantity = variantQuantities[variant.id] || 0;
      // Use original_price instead of price to show undiscounted subtotal
      const price = parseFloat(variant.original_price || variant.price);
      return total + (price * quantity);
    }, 0);
  };

  const subtotal = calculateSubtotal();
  
  // Calculate discount amount based on user's discount percentage
  const userDiscount = getUserDiscount();
  const discountAmount = subtotal * (userDiscount / 100);
  const subtotalAfterDiscount = subtotal - discountAmount;
  
  // Calculate payment processor fee based on selected method
  const getProcessorFeePercent = () => {
    if (selectedPaymentMethod === 'btc' || selectedPaymentMethod === 'ltc') return 0; // 0% for crypto
    if (selectedPaymentMethod === 'balance') return 0; // 0% for balance
    return 0; // Default 0%
  };
  
  const processorFeePercent = getProcessorFeePercent();
  const paymentProcessorFee = subtotalAfterDiscount * (processorFeePercent / 100);
  const total = subtotalAfterDiscount + paymentProcessorFee;

  // Get payment method display name
  const getPaymentMethodName = () => {
    if (selectedPaymentMethod === 'balance') return 'Balance';
    if (selectedPaymentMethod === 'btc') return 'Bitcoin';
    if (selectedPaymentMethod === 'ltc') return 'Litecoin';
    if (selectedPaymentMethod === 'method4') return 'Method 4';
    return 'Payment Method';
  };

  // Handle Buy Now button click
  const handleBuyNow = async () => {
    if (!selectedPaymentMethod) {
      alert('Please select a payment method');
      return;
    }

    // Check if any variants have quantity > 0
    const hasItems = Object.values(variantQuantities).some(qty => qty > 0);
    if (!hasItems) {
      alert('Please select at least one product variant');
      return;
    }

    // Check stock availability for manual stock variants
    const token = sessionStorage.getItem('token');
    if (!token) {
      alert('Please login to continue');
      return;
    }

    const outOfStock = [];
    for (const [variantId, quantity] of Object.entries(variantQuantities)) {
      if (quantity > 0) {
        const variant = selectedProduct.variants.find(v => v.id === parseInt(variantId));
        if (variant && variant.stock_type === 'manual') {
          // Check available stock
          try {
            const stockResponse = await fetch(`/api/admin/stock/variant/${variantId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            const stockData = await stockResponse.json();
            if (stockData.success) {
              const availableCount = stockData.data.filter(k => k.status === 'available').length;
              if (availableCount < quantity) {
                outOfStock.push({
                  name: variant.name,
                  requested: quantity,
                  available: availableCount
                });
              }
            }
          } catch (error) {
            console.error('Error checking stock:', error);
          }
        }
      }
    }

    // If any variants are out of stock, show modal and stop
    if (outOfStock.length > 0) {
      setOutOfStockVariants(outOfStock);
      setShowOutOfStockModal(true);
      return;
    }

    // Handle crypto payments (BTC/LTC)
    if (selectedPaymentMethod === 'btc' || selectedPaymentMethod === 'ltc') {
      try {
        // Get auth token from sessionStorage (not localStorage)
        const token = sessionStorage.getItem('token');
        if (!token) {
          alert('Please login to continue');
          return;
        }

        // Get the first variant with quantity > 0 (for now, BTCPay only supports single variant per invoice)
        const selectedVariantId = Object.entries(variantQuantities).find(([id, qty]) => qty > 0)?.[0];
        const selectedVariantQty = variantQuantities[selectedVariantId];
        
        if (!selectedVariantId) {
          alert('Please select a product variant');
          return;
        }

        // Create BTCPay invoice
        const response = await fetch('/api/btcpay/create-invoice', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            productId: parseInt(selectedVariantId), // This is actually the variant ID (backend expects it)
            quantity: selectedVariantQty,
            amount: total,
            currency: 'USD',
            paymentMethod: selectedPaymentMethod // 'btc' or 'ltc'
          })
        });

        const data = await response.json();

        if (data.success && data.data.checkoutLink) {
          // Redirect to BTCPay checkout
          window.location.href = data.data.checkoutLink;
        } else {
          alert('Failed to create payment invoice: ' + (data.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Payment error:', error);
        alert('Failed to process payment. Please try again.');
      }
    } else if (selectedPaymentMethod === 'balance') {
      // Handle balance payment
      try {
        const token = sessionStorage.getItem('token');
        if (!token) {
          alert('Please login to continue');
          return;
        }

        // Check if user has sufficient balance
        if (user.balance < total) {
          const shortfall = (total - user.balance).toFixed(2);
          setInsufficientBalanceData({
            required: total.toFixed(2),
            available: user.balance.toFixed(2),
            shortfall: shortfall
          });
          setShowInsufficientBalanceModal(true);
          return;
        }

        // Prepare variants array for purchase
        const variantsArray = Object.entries(variantQuantities)
          .filter(([_, qty]) => qty > 0)
          .map(([variantId, quantity]) => ({
            variant_id: parseInt(variantId),
            quantity: quantity
          }));

        // Create balance purchase
        const response = await fetch('/api/orders/balance-purchase', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            variants: variantsArray
          })
        });

        const data = await response.json();

        if (data.success) {
          setPurchaseResult({
            success: true,
            message: data.message,
            totalCost: data.data.total_cost,
            previousBalance: data.data.previous_balance,
            newBalance: data.data.new_balance,
            discountApplied: data.data.discount_applied,
            orders: data.data.orders
          });
          
          // Reset selections
          setVariantQuantities({});
          setSelectedPaymentMethod(null);
        } else {
          if (data.message === 'Insufficient balance') {
            setPurchaseResult({
              success: false,
              type: 'insufficient',
              required: data.data.required,
              available: data.data.available,
              shortfall: data.data.shortfall
            });
          } else {
            setPurchaseResult({
              success: false,
              type: 'error',
              message: data.message || 'Unknown error'
            });
          }
        }
      } catch (error) {
        console.error('Balance payment error:', error);
        alert('Failed to process balance payment. Please try again.');
      }
    } else {
      alert('Payment method not yet implemented');
    }
  };

  // Handle topup
  const handleTopup = async () => {
    if (!topupPaymentMethod) {
      alert('Please select a payment method');
      return;
    }

    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        alert('Please login to continue');
        return;
      }

      // Create BTCPay invoice for balance topup
      const response = await fetch('/api/btcpay/create-topup-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: amount,
          currency: 'USD',
          paymentMethod: topupPaymentMethod
        })
      });

      const data = await response.json();

      if (data.success && data.data.checkoutLink) {
        // Redirect to BTCPay checkout
        window.location.href = data.data.checkoutLink;
      } else {
        alert('Failed to create topup invoice: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Topup error:', error);
      alert('Failed to process topup. Please try again.');
    }
  };

  return (
    <div className="h-full pt-7" style={{ marginLeft: '-1.75rem', marginRight: '-1.75rem', paddingLeft: '1.75rem', paddingRight: '1.75rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      <div className="flex gap-7">
        {/* Main Products Box */}
        <div className="product-box-container">
          <div className="card product-box" style={{ height: 'calc(80vh - 48px)', maxWidth: '383px', display: 'flex', flexDirection: 'column' }}>
        {/* Search and Category Header */}
        <div className="p-6 border-b" style={{ borderColor: '#4a4a4a' }}>
          <div className="flex gap-4">
            {/* Search Bar - 50% */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg text-gray-100 placeholder-gray-400"
                  style={{ 
                    backgroundColor: '#0a0a0a', 
                    border: '1px solid #4a4a4a',
                    outline: 'none',
                    transition: 'box-shadow 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.boxShadow = '0 0 0 2px #3b198e';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = '';
                  }}
                />
              </div>
            </div>

            {/* Category Selector - 50% */}
            <div className="flex-1 category-dropdown-container" style={{ minWidth: 0, position: 'relative' }}>
              <div
                onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                style={{ 
                  width: '100%',
                  backgroundColor: '#0a0a0a', 
                  border: '1px solid #4a4a4a',
                  borderRadius: '8px',
                  paddingTop: '0.5rem',
                  paddingBottom: '0.5rem',
                  paddingLeft: '1rem',
                  paddingRight: '2.5rem',
                  cursor: 'pointer',
                  position: 'relative',
                  color: '#ffffff',
                  transition: 'border-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
              >
                {selectedCategory || 'All Categories'}
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="#ffffff" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: categoryDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                    width: '1.25rem',
                    height: '1.25rem',
                    transition: 'transform 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              
              {categoryDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #4a4a4a',
                    borderRadius: '8px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    animation: 'dropdownSlide 0.2s ease-out'
                  }}
                  className="variants-scrollbar"
                >
                  <div
                    onClick={() => {
                      setSelectedCategory('');
                      setCategoryDropdownOpen(false);
                    }}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      color: '#ffffff',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    All Categories
                  </div>
                  {categories.map(category => (
                    <div
                      key={category}
                      onClick={() => {
                        setSelectedCategory(category);
                        setCategoryDropdownOpen(false);
                      }}
                      style={{
                        padding: '0.75rem 1rem',
                        cursor: 'pointer',
                        color: '#ffffff',
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {category}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="variants-scrollbar" style={{ overflowY: 'overlay', height: '100%' }}>
          <div style={{ paddingLeft: '20px', paddingRight: '24px', paddingTop: '20px', paddingBottom: '24px' }}>
          {loading ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>Loading products...</div>
          ) : filteredProducts.length === 0 ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>No products found</div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
            {filteredProducts.map((product) => {
              const statusInfo = getStatusInfo(product.status);
              const StatusIcon = statusInfo.icon;
              const isActive = selectedProduct && selectedProduct.id === product.id;
              
              return (
                <div 
                  key={product.id}
                  onClick={() => setSelectedProduct(product)}
                  style={{
                    backgroundColor: '#1a1a1a',
                    paddingTop: '13px',
                    paddingBottom: '13px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    userSelect: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#252525'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1a1a1a'}
                >
                  {/* Status Icon - Left Side */}
                  <StatusIcon 
                    className="h-5 w-5"
                    style={{ color: statusInfo.color, cursor: 'pointer', pointerEvents: 'none' }}
                  />

                  {/* Product Name */}
                  <h3 
                    className="text-base"
                    style={{ 
                      color: isActive ? '#3b198e' : '#ffffff',
                      cursor: 'pointer',
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {product.name}
                    {product.featured === 1 && (
                      <Star 
                        className="h-4 w-4" 
                        style={{ color: '#fbbf24', fill: 'none', strokeWidth: 2 }}
                      />
                    )}
                  </h3>
                </div>
              );
            })}
          </div>
          )}
          </div>
        </div>
        </div>
        </div>

        {/* Right Side Column */}
        <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {/* Top Right Box */}
          <div className="product-box-container">
            <div 
              className="card product-box" 
              style={{ 
                height: '80px'
              }}
            >
              <div className="p-6 h-full flex items-center justify-between">
                {/* Product Name - Top Left */}
                <h3 className="text-gray-100 font-bold text-xl">
                  {selectedProduct ? selectedProduct.name : 'Loading...'}
                </h3>

                {/* Status Word + Icon - Right Side, Centered Vertically */}
                {selectedProduct && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium" style={{ color: getStatusInfo(selectedProduct.status).color }}>
                      {selectedProduct.status}
                    </span>
                    {(() => {
                      const StatusIcon = getStatusInfo(selectedProduct.status).icon;
                      return (
                        <StatusIcon 
                          className="h-5 w-5"
                          style={{ color: getStatusInfo(selectedProduct.status).color }}
                        />
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Variants Box Below Top Right */}
          <div className="product-box-container">
            <div 
              className="card product-box" 
              style={{ 
                height: 'calc(80vh - 156px)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Scrollable content area */}
              <div className="variants-scrollbar" style={{ overflowY: 'overlay', height: '100%' }}>
                <div className="p-6">
                  <h3 className="text-gray-100 font-bold text-lg">Variants</h3>
                  
                  {/* Separator Line */}
                  <div style={{ 
                    height: '1px', 
                    backgroundColor: '#4a4a4a', 
                    marginTop: '16px',
                    marginBottom: '16px'
                  }} />
                  
                  {/* Variants List */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedProduct && selectedProduct.variants && selectedProduct.variants.length > 0 ? (
                    selectedProduct.variants.map((variant) => {
                      const quantity = variantQuantities[variant.id] || 0;
                      const originalPrice = parseFloat(variant.original_price || variant.price);
                      const discountedPrice = parseFloat(variant.price);

                      return (
                        <div key={variant.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          {/* Left side - Variant info */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className="text-base" style={{ color: '#ffffff' }}>{variant.name}</span>
                              <span className="text-base" style={{ color: '#9ca3af', textDecoration: userDiscountPercent > 0 ? 'line-through' : 'none' }}>${originalPrice.toFixed(2)}</span>
                            </div>
                            {userDiscountPercent > 0 && (
                              <span className="text-sm" style={{ color: '#3b198e' }}>
                                Discount: ${discountedPrice.toFixed(2)}/key
                              </span>
                            )}
                          </div>
                          
                          {/* Right side - Quantity selector */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Minus button */}
                            <button
                              onClick={() => decrementQuantity(variant.id)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#3b198e';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                              style={{
                                backgroundColor: 'transparent',
                                color: '#9ca3af',
                                border: 'none',
                                fontSize: '18px',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                userSelect: 'none',
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background-color 0.2s ease'
                              }}
                            >-</button>
                            
                            {/* Quantity input */}
                            <input
                              type="number"
                              min="0"
                              value={quantity}
                              onChange={(e) => handleQuantityChange(variant.id, e.target.value)}
                              className="quantity-input"
                              style={{
                                backgroundColor: '#0a0a0a',
                                border: '1px solid #4a4a4a',
                                borderRadius: '6px',
                                padding: '4px 0px',
                                minWidth: '50px',
                                width: '60px',
                                textAlign: 'center',
                                color: '#ffffff',
                                fontSize: '14px',
                                outline: 'none',
                                MozAppearance: 'textfield',
                                WebkitAppearance: 'none',
                                appearance: 'none'
                              }}
                            />
                            
                            {/* Plus button */}
                            <button
                              onClick={() => incrementQuantity(variant.id)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#3b198e';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                              style={{
                                backgroundColor: 'transparent',
                                color: '#9ca3af',
                                border: 'none',
                                fontSize: '18px',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                userSelect: 'none',
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background-color 0.2s ease'
                              }}
                            >+</button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <span className="text-gray-400 text-sm">No variants available</span>
                  )}
                  </div>
                  
                  {/* Bottom Separator Line */}
                  {selectedProduct && selectedProduct.variants && selectedProduct.variants.length > 0 && (
                    <div style={{ 
                      height: '1px', 
                      backgroundColor: '#4a4a4a', 
                      marginTop: '16px',
                      marginBottom: '16px'
                    }} />
                  )}
                  
                  {/* Select Payment Method Text */}
                  <span className="text-gray-400 text-base">Select Payment Method</span>
                
                {/* 2x2 Grid of Payment Method Rectangles */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '12px',
                  marginTop: '16px'
                }}>
                  {/* Rectangle 1 - Balance */}
                  <div 
                    key={`balance-${selectedPaymentMethod}`}
                    onClick={() => setSelectedPaymentMethod('balance')}
                    onMouseEnter={(e) => {
                      if (selectedPaymentMethod !== 'balance') {
                        e.currentTarget.style.backgroundColor = '#252525';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedPaymentMethod !== 'balance') {
                        e.currentTarget.style.backgroundColor = '#1a1a1a';
                      }
                    }}
                    style={{
                      backgroundColor: selectedPaymentMethod === 'balance' ? '#1a1a1a' : '#1a1a1a',
                      borderRadius: '8px',
                      height: '70px',
                      border: selectedPaymentMethod === 'balance' ? '1px solid #3b198e' : '1px solid #4a4a4a',
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease, border-color 0.15s ease'
                    }}>
                    {/* Left side - Balance info */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {/* Balance icon and text */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Wallet className="h-5 w-5" style={{ color: '#3b198e' }} />
                        <span className="text-base font-bold" style={{ color: '#e5e5e5' }}>Balance</span>
                      </div>
                      {/* Your Balance amount */}
                      <span className="text-sm" style={{ color: '#9ca3af' }}>Your Balance: ${(user?.balance || 0).toFixed(2)}</span>
                    </div>
                    
                    {/* Right side - Topup button */}
                    <button 
                      style={{
                        backgroundColor: '#3b198e',
                        color: '#ffffff',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '15px',
                        fontWeight: '500',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#2d1270';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#3b198e';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTopupModal(true);
                      }}
                    >Topup</button>
                  </div>
                  
                  {/* Rectangle 2 - Bitcoin */}
                  <div 
                    key={`btc-${selectedPaymentMethod}`}
                    onClick={() => setSelectedPaymentMethod('btc')}
                    onMouseEnter={(e) => {
                      if (selectedPaymentMethod !== 'btc') {
                        e.currentTarget.style.backgroundColor = '#252525';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedPaymentMethod !== 'btc') {
                        e.currentTarget.style.backgroundColor = '#1a1a1a';
                      }
                    }}
                    style={{
                      backgroundColor: selectedPaymentMethod === 'btc' ? '#1a1a1a' : '#1a1a1a',
                      borderRadius: '8px',
                      height: '70px',
                      border: selectedPaymentMethod === 'btc' ? '1px solid #3b198e' : '1px solid #4a4a4a',
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease, border-color 0.15s ease'
                    }}>
                    {/* Left side - Bitcoin info */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {/* Bitcoin icon and text */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Bitcoin className="h-5 w-5" style={{ color: '#f7931a' }} />
                        <span className="text-base font-bold" style={{ color: '#e5e5e5' }}>Bitcoin</span>
                      </div>
                      {/* Processing Fee */}
                      <span className="text-sm" style={{ color: '#9ca3af' }}>Processing Fee: 0%</span>
                    </div>
                  </div>
                  
                  {/* Rectangle 3 - Litecoin */}
                  <div 
                    key={`ltc-${selectedPaymentMethod}`}
                    onClick={() => setSelectedPaymentMethod('ltc')}
                    onMouseEnter={(e) => {
                      if (selectedPaymentMethod !== 'ltc') {
                        e.currentTarget.style.backgroundColor = '#252525';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedPaymentMethod !== 'ltc') {
                        e.currentTarget.style.backgroundColor = '#1a1a1a';
                      }
                    }}
                    style={{
                      backgroundColor: selectedPaymentMethod === 'ltc' ? '#1a1a1a' : '#1a1a1a',
                      borderRadius: '8px',
                      height: '70px',
                      border: selectedPaymentMethod === 'ltc' ? '1px solid #3b198e' : '1px solid #4a4a4a',
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease, border-color 0.15s ease'
                    }}>
                    {/* Left side - Litecoin info */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {/* Litecoin icon and text */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Bitcoin className="h-5 w-5" style={{ color: '#345d9d' }} />
                        <span className="text-base font-bold" style={{ color: '#e5e5e5' }}>Litecoin</span>
                      </div>
                      {/* Processing Fee */}
                      <span className="text-sm" style={{ color: '#9ca3af' }}>Processing Fee: 0%</span>
                    </div>
                  </div>
                  
                  {/* Rectangle 4 */}
                  <div 
                    key={`method4-${selectedPaymentMethod}`}
                    onClick={() => setSelectedPaymentMethod('method4')}
                    onMouseEnter={(e) => {
                      if (selectedPaymentMethod !== 'method4') {
                        e.currentTarget.style.backgroundColor = '#252525';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedPaymentMethod !== 'method4') {
                        e.currentTarget.style.backgroundColor = '#1a1a1a';
                      }
                    }}
                    style={{
                      backgroundColor: selectedPaymentMethod === 'method4' ? '#1a1a1a' : '#1a1a1a',
                      borderRadius: '8px',
                      height: '70px',
                      border: selectedPaymentMethod === 'method4' ? '1px solid #3b198e' : '1px solid #4a4a4a',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease, border-color 0.15s ease'
                    }} />
                </div>
                
                {/* Pricing Breakdown */}
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {/* Subtotal (undiscounted) */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="text-sm" style={{ color: '#9ca3af' }}>Subtotal</span>
                    <span className="text-sm" style={{ color: '#9ca3af' }}>${subtotal.toFixed(2)}</span>
                  </div>
                  
                  {/* Discount */}
                  {userDiscount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="text-sm" style={{ color: '#22c55e' }}>Discount ({userDiscount}%)</span>
                      <span className="text-sm" style={{ color: '#22c55e' }}>-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {/* Payment processor fee */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="text-sm" style={{ color: '#9ca3af' }}>Payment processor fee:</span>
                    <span className="text-sm" style={{ color: '#9ca3af' }}>${paymentProcessorFee.toFixed(2)}</span>
                  </div>
                  
                  {/* Total */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="text-base font-medium" style={{ color: '#ffffff' }}>Total:</span>
                    <span className="text-base font-medium" style={{ color: '#ffffff' }}>${total.toFixed(2)}</span>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button 
                    onClick={handleBuyNow}
                    style={{
                      flex: 1,
                      backgroundColor: '#3b198e',
                      color: '#ffffff',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '15px',
                      fontWeight: '500',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#2d1270';
                      e.currentTarget.style.transform = 'scaleX(1.02) scaleY(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#3b198e';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >Buy Now with {getPaymentMethodName()}</button>
                  
                  <button 
                    onClick={() => {
                      if (!selectedProduct) return;
                      
                      // Check if user has API key first
                      if (!hasApiKey) {
                        alert('Please generate an API key on the Account page first before accessing API links.');
                        return;
                      }
                      
                      // Show confirmation modal
                      setShowApiLinksConfirmModal(true);
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: '#3b198e',
                      color: '#ffffff',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '15px',
                      fontWeight: '500',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#2d1270';
                      e.currentTarget.style.transform = 'scaleX(1.02) scaleY(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#3b198e';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >Fetch API Links</button>
                </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Description Box - Full Width at Bottom - Always Visible */}
      {selectedProduct && (
        <div className="product-box-container">
          <div 
            className="card product-box" 
            style={{ 
              minHeight: '120px',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div className="p-6">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="text-gray-100 font-bold text-lg">Description</h3>
                {(() => {
                  const descData = selectedProduct.description_data 
                    ? (typeof selectedProduct.description_data === 'string' 
                        ? JSON.parse(selectedProduct.description_data) 
                        : selectedProduct.description_data)
                    : null;
                  
                  const hasAnyContent = descData && (
                    descData.requirements || 
                    descData.information || 
                    descData.aimbot || 
                    descData.visuals || 
                    descData.world || 
                    descData.configs || 
                    descData.misc
                  );

                  if (hasAnyContent) {
                    return (
                      <button
                        onClick={() => copyAllDescription(descData)}
                        style={{
                          background: 'none',
                          border: '1px solid #4a4a4a',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          color: '#9ca3af',
                          fontSize: '14px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {copiedFields.copyAll ? <Check size={16} color="#10b981" /> : <Copy size={16} />}
                        <span>Copy All</span>
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
              
              {/* Separator Line */}
              <div style={{ 
                height: '1px', 
                backgroundColor: '#4a4a4a', 
                marginTop: '16px',
                marginBottom: '16px'
              }} />
              
              {/* Description Content - Sub-boxes for each field */}
              {(() => {
                const descData = selectedProduct.description_data 
                  ? (typeof selectedProduct.description_data === 'string' 
                      ? JSON.parse(selectedProduct.description_data) 
                      : selectedProduct.description_data)
                  : null;
                
                const hasAnyContent = descData && (
                  descData.requirements || 
                  descData.information || 
                  descData.aimbot || 
                  descData.visuals || 
                  descData.world || 
                  descData.configs || 
                  descData.misc
                );

                if (!hasAnyContent) {
                  return (
                    <p style={{ color: '#9ca3af', fontSize: '14px', fontStyle: 'italic', margin: 0 }}>
                      No description available
                    </p>
                  );
                }

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {descData.requirements && (
                      <div style={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        padding: '16px',
                        position: 'relative'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                            Requirements
                          </h4>
                          <button
                            onClick={() => copyToClipboard(descData.requirements, 'requirements')}
                            style={{
                              background: 'none',
                              border: '1px solid #4a4a4a',
                              borderRadius: '4px',
                              padding: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {copiedFields.requirements ? <Check size={16} color="#10b981" /> : <Copy size={16} color="#9ca3af" />}
                          </button>
                        </div>
                        <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                          {formatTextWithBullets(descData.requirements)}
                        </div>
                      </div>
                    )}

                    {descData.information && (
                      <div style={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        padding: '16px',
                        position: 'relative'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                            Information
                          </h4>
                          <button
                            onClick={() => copyToClipboard(descData.information, 'information')}
                            style={{
                              background: 'none',
                              border: '1px solid #4a4a4a',
                              borderRadius: '4px',
                              padding: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {copiedFields.information ? <Check size={16} color="#10b981" /> : <Copy size={16} color="#9ca3af" />}
                          </button>
                        </div>
                        <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                          {formatTextWithBullets(descData.information)}
                        </div>
                      </div>
                    )}

                    {descData.aimbot && (
                      <div style={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        padding: '16px',
                        position: 'relative'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                            Aimbot
                          </h4>
                          <button
                            onClick={() => copyToClipboard(descData.aimbot, 'aimbot')}
                            style={{
                              background: 'none',
                              border: '1px solid #4a4a4a',
                              borderRadius: '4px',
                              padding: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {copiedFields.aimbot ? <Check size={16} color="#10b981" /> : <Copy size={16} color="#9ca3af" />}
                          </button>
                        </div>
                        <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                          {formatTextWithBullets(descData.aimbot)}
                        </div>
                      </div>
                    )}

                    {descData.visuals && (
                      <div style={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        padding: '16px',
                        position: 'relative'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                            Visuals
                          </h4>
                          <button
                            onClick={() => copyToClipboard(descData.visuals, 'visuals')}
                            style={{
                              background: 'none',
                              border: '1px solid #4a4a4a',
                              borderRadius: '4px',
                              padding: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {copiedFields.visuals ? <Check size={16} color="#10b981" /> : <Copy size={16} color="#9ca3af" />}
                          </button>
                        </div>
                        <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                          {formatTextWithBullets(descData.visuals)}
                        </div>
                      </div>
                    )}

                    {descData.world && (
                      <div style={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        padding: '16px',
                        position: 'relative'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                            World
                          </h4>
                          <button
                            onClick={() => copyToClipboard(descData.world, 'world')}
                            style={{
                              background: 'none',
                              border: '1px solid #4a4a4a',
                              borderRadius: '4px',
                              padding: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {copiedFields.world ? <Check size={16} color="#10b981" /> : <Copy size={16} color="#9ca3af" />}
                          </button>
                        </div>
                        <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                          {formatTextWithBullets(descData.world)}
                        </div>
                      </div>
                    )}

                    {descData.configs && (
                      <div style={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        padding: '16px',
                        position: 'relative'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                            Configs
                          </h4>
                          <button
                            onClick={() => copyToClipboard(descData.configs, 'configs')}
                            style={{
                              background: 'none',
                              border: '1px solid #4a4a4a',
                              borderRadius: '4px',
                              padding: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {copiedFields.configs ? <Check size={16} color="#10b981" /> : <Copy size={16} color="#9ca3af" />}
                          </button>
                        </div>
                        <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                          {formatTextWithBullets(descData.configs)}
                        </div>
                      </div>
                    )}

                    {descData.misc && (
                      <div style={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        padding: '16px',
                        position: 'relative'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                            Misc
                          </h4>
                          <button
                            onClick={() => copyToClipboard(descData.misc, 'misc')}
                            style={{
                              background: 'none',
                              border: '1px solid #4a4a4a',
                              borderRadius: '4px',
                              padding: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {copiedFields.misc ? <Check size={16} color="#10b981" /> : <Copy size={16} color="#9ca3af" />}
                          </button>
                        </div>
                        <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                          {formatTextWithBullets(descData.misc)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Out of Stock Modal */}
      {showOutOfStockModal && outOfStockVariants.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={() => {
                setShowOutOfStockModal(false);
                setOutOfStockVariants([]);
              }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
            >
              <X className="h-6 w-6" />
            </button>

            {/* Modal content */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <AlertTriangle style={{ width: '64px', height: '64px', color: '#ef4444', margin: '0 auto 16px' }} />
              <h2 style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                Out of Stock
              </h2>
              <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '24px' }}>
                The following variants are currently out of stock.
              </p>
            </div>

            {/* Out of stock variants list */}
            <div style={{ 
              backgroundColor: '#0a0a0a', 
              border: '1px solid #4a4a4a',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              {outOfStockVariants.map((variant, index) => (
                <div key={index} style={{ 
                  marginBottom: index < outOfStockVariants.length - 1 ? '12px' : '0',
                  paddingBottom: index < outOfStockVariants.length - 1 ? '12px' : '0',
                  borderBottom: index < outOfStockVariants.length - 1 ? '1px solid #4a4a4a' : 'none'
                }}>
                  <div style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                    {variant.name}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#9ca3af', fontSize: '13px' }}>Requested:</span>
                    <span style={{ color: '#ef4444', fontSize: '13px', fontWeight: '600' }}>
                      {variant.requested}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#9ca3af', fontSize: '13px' }}>Available:</span>
                    <span style={{ color: '#9ca3af', fontSize: '13px', fontWeight: '600' }}>
                      {variant.available}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Close button */}
            <button
              onClick={() => {
                setShowOutOfStockModal(false);
                setOutOfStockVariants([]);
              }}
              style={{
                width: '100%',
                backgroundColor: '#3b198e',
                color: '#ffffff',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2d1270';
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3b198e';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Insufficient Balance Modal */}
      {showInsufficientBalanceModal && insufficientBalanceData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={() => {
                setShowInsufficientBalanceModal(false);
                setInsufficientBalanceData(null);
              }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
            >
              <X className="h-6 w-6" />
            </button>

            {/* Modal content */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <AlertTriangle style={{ width: '64px', height: '64px', color: '#eab308', margin: '0 auto 16px' }} />
              <h2 style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                Insufficient Balance
              </h2>
              <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '24px' }}>
                You don't have enough balance to complete this purchase.
              </p>
            </div>

            {/* Balance details */}
            <div style={{ 
              backgroundColor: '#0a0a0a', 
              border: '1px solid #4a4a4a',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ color: '#9ca3af', fontSize: '14px' }}>Required:</span>
                <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>
                  ${insufficientBalanceData.required}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ color: '#9ca3af', fontSize: '14px' }}>Available:</span>
                <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>
                  ${insufficientBalanceData.available}
                </span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                paddingTop: '12px',
                borderTop: '1px solid #4a4a4a'
              }}>
                <span style={{ color: '#9ca3af', fontSize: '14px' }}>Shortfall:</span>
                <span style={{ color: '#ef4444', fontSize: '14px', fontWeight: '600' }}>
                  ${insufficientBalanceData.shortfall}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowInsufficientBalanceModal(false);
                  setInsufficientBalanceData(null);
                }}
                style={{
                  flex: 1,
                  backgroundColor: '#0a0a0a',
                  color: '#9ca3af',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  border: '1px solid #4a4a4a',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1a1a1a';
                  e.currentTarget.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#0a0a0a';
                  e.currentTarget.style.color = '#9ca3af';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowInsufficientBalanceModal(false);
                  setInsufficientBalanceData(null);
                  setShowTopupModal(true);
                }}
                style={{
                  flex: 1,
                  backgroundColor: '#3b198e',
                  color: '#ffffff',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2d1270';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3b198e';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                Top Up Balance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topup Modal */}
      {showTopupModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={() => {
                setShowTopupModal(false);
                setTopupAmount('');
                setTopupPaymentMethod(null);
              }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
            >
              <X className="h-6 w-6" />
            </button>

            {/* Modal title */}
            <h2 style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
              Top Up Balance
            </h2>

            {/* Amount input */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Amount (USD)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                placeholder="Enter amount"
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Payment method selection */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '12px' }}>
                Select Payment Method
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {/* Bitcoin */}
                <div
                  onClick={() => setTopupPaymentMethod('btc')}
                  style={{
                    backgroundColor: topupPaymentMethod === 'btc' ? '#1a1a1a' : '#1a1a1a',
                    border: topupPaymentMethod === 'btc' ? '2px solid #3b198e' : '1px solid #4a4a4a',
                    borderRadius: '8px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (topupPaymentMethod !== 'btc') {
                      e.currentTarget.style.backgroundColor = '#252525';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (topupPaymentMethod !== 'btc') {
                      e.currentTarget.style.backgroundColor = '#1a1a1a';
                    }
                  }}
                >
                  <Bitcoin className="h-8 w-8" style={{ color: '#f7931a' }} />
                  <span style={{ color: '#e5e5e5', fontSize: '16px', fontWeight: '500' }}>Bitcoin</span>
                </div>

                {/* Litecoin */}
                <div
                  onClick={() => setTopupPaymentMethod('ltc')}
                  style={{
                    backgroundColor: topupPaymentMethod === 'ltc' ? '#1a1a1a' : '#1a1a1a',
                    border: topupPaymentMethod === 'ltc' ? '2px solid #3b198e' : '1px solid #4a4a4a',
                    borderRadius: '8px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (topupPaymentMethod !== 'ltc') {
                      e.currentTarget.style.backgroundColor = '#252525';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (topupPaymentMethod !== 'ltc') {
                      e.currentTarget.style.backgroundColor = '#1a1a1a';
                    }
                  }}
                >
                  <Bitcoin className="h-8 w-8" style={{ color: '#345d9d' }} />
                  <span style={{ color: '#e5e5e5', fontSize: '16px', fontWeight: '500' }}>Litecoin</span>
                </div>
              </div>
            </div>

            {/* Confirm button */}
            <button
              onClick={handleTopup}
              style={{
                width: '100%',
                backgroundColor: '#3b198e',
                color: '#ffffff',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2d1270';
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3b198e';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              Continue to Payment
            </button>
          </div>
        </div>
      )}

      {/* API Links Modal */}
      {showApiLinksModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => {
                setShowApiLinksModal(false);
                setApiLinks(null);
                setSelectedProductForApiLinks(null);
              }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X className="h-6 w-6" />
            </button>

            <h2 style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
              API Links - {selectedProductForApiLinks?.name}
            </h2>
            <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '24px' }}>
              Your unique API links for each variant. These links are generated based on your API key.
            </p>

            {isLoadingApiLinks ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                Loading API links...
              </div>
            ) : apiLinks && Object.keys(apiLinks).length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {Object.entries(apiLinks).map(([productName, productData]) => (
                  <div key={productName} style={{
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #4a4a4a',
                    borderRadius: '8px',
                    padding: '16px'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {productData.variants.map((variant) => (
                        <div key={variant.variantId} style={{
                          backgroundColor: '#1a1a1a',
                          border: '1px solid #4a4a4a',
                          borderRadius: '6px',
                          padding: '12px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: '500' }}>
                              {variant.variantName}
                            </span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(variant.apiLink);
                                setCopiedVariantId(variant.variantId);
                                setTimeout(() => setCopiedVariantId(null), 2000);
                              }}
                              style={{
                                backgroundColor: copiedVariantId === variant.variantId ? '#10b981' : '#3b198e',
                                border: 'none',
                                color: '#ffffff',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'background-color 0.2s'
                              }}
                            >
                              {copiedVariantId === variant.variantId ? (
                                <>
                                  <CheckCircle className="h-3 w-3" />
                                  Copied!
                                </>
                              ) : (
                                'Copy'
                              )}
                            </button>
                          </div>
                          <code style={{
                            display: 'block',
                            color: '#9ca3af',
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            backgroundColor: '#0a0a0a',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #4a4a4a',
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word'
                          }}>
                            {variant.apiLink}
                          </code>
                          {variant.prefix !== 'Default format' && (
                            <p style={{ color: '#6b7280', fontSize: '10px', marginTop: '4px' }}>
                              Format: {variant.prefix}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                No API links available. Make sure you have an API key generated.
              </div>
            )}
          </div>
        </div>
      )}

      {/* API Links Confirmation Modal */}
      {showApiLinksConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '16px',
            padding: '32px',
            width: '90%',
            maxWidth: '550px',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowApiLinksConfirmModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
            >
              <X size={24} />
            </button>

            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <AlertTriangle style={{ width: '72px', height: '72px', color: '#3b198e', margin: '0 auto 20px' }} />
              <h2 style={{ color: '#ffffff', fontSize: '26px', fontWeight: 'bold', marginBottom: '12px' }}>
                Important Security Warning
              </h2>
            </div>

            <div style={{
              backgroundColor: '#0f0f0f',
              border: '1px solid #4a4a4a',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                🔒 Why This Matters:
              </h3>
              <ul style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px', margin: 0 }}>
                <li style={{ marginBottom: '12px' }}>
                  <strong style={{ color: '#ffffff' }}>Links are permanent</strong> - Once generated, they remain active until your API key changes
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <strong style={{ color: '#ffffff' }}>Sharing = Risk</strong> - Only share with trusted parties. Leaked links = unauthorized charges
                </li>
                <li>
                  <strong style={{ color: '#ffffff' }}>Regeneration required</strong> - If compromised, you must immediately regenerate your API key on the Account page
                </li>
              </ul>
            </div>

            <div style={{
              backgroundColor: '#0a0a0a',
              border: '1px solid #4a4a4a',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ color: '#ffffff', fontSize: '13px', margin: 0, lineHeight: '1.6' }}>
                <strong>Recommendation:</strong> Only fetch API links if you absolutely need them for integration purposes. 
                For regular purchases, use the "Buy Now" button instead.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowApiLinksConfirmModal(false)}
                style={{
                  flex: 1,
                  backgroundColor: '#2a2a2a',
                  color: '#ffffff',
                  padding: '14px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: '1px solid #4a4a4a',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#3a3a3a';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#2a2a2a';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowApiLinksConfirmModal(false);
                  
                  if (!selectedProduct) return;
                  
                  setIsLoadingApiLinks(true);
                  setShowApiLinksModal(true);
                  setSelectedProductForApiLinks(selectedProduct);
                  try {
                    const response = await fetch(`/api/api-links?productId=${selectedProduct.id}`, {
                      headers: {
                        'Authorization': `Bearer ${sessionStorage.getItem('token')}`
                      }
                    });
                    const data = await response.json();
                    console.log('API Links Response:', data);
                    if (data.success) {
                      setApiLinks(data.data);
                    } else {
                      alert(data.message || 'Failed to fetch API links');
                      console.error('API Links Error:', data);
                    }
                  } catch (error) {
                    console.error('API Links Fetch Error:', error);
                    alert('Failed to fetch API links');
                  } finally {
                    setIsLoadingApiLinks(false);
                  }
                }}
                style={{
                  flex: 1,
                  backgroundColor: '#3b198e',
                  color: '#ffffff',
                  padding: '14px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2d1270';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3b198e';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                I Understand, Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Result Modal */}
      {purchaseResult && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '32px',
            width: '90%',
            maxWidth: '500px',
            position: 'relative'
          }}>
            <button
              onClick={() => setPurchaseResult(null)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X className="h-6 w-6" />
            </button>

            {purchaseResult.success ? (
              <>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <CheckCircle style={{ width: '64px', height: '64px', color: '#22c55e', margin: '0 auto 16px' }} />
                  <h2 style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                    Purchase Successful!
                  </h2>
                  <p style={{ color: '#9ca3af', fontSize: '14px' }}>
                    {purchaseResult.message}
                  </p>
                </div>

                <div style={{
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>Total Cost:</span>
                      <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>
                        ${parseFloat(purchaseResult.totalCost).toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>Previous Balance:</span>
                      <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>
                        ${parseFloat(purchaseResult.previousBalance).toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>New Balance:</span>
                      <span style={{ color: '#22c55e', fontSize: '14px', fontWeight: '600' }}>
                        ${parseFloat(purchaseResult.newBalance).toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>Discount Applied:</span>
                      <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>
                        {purchaseResult.discountApplied}%
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{
                  backgroundColor: '#1e3a8a',
                  border: '1px solid #3b82f6',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '24px'
                }}>
                  <p style={{ color: '#93c5fd', fontSize: '13px', margin: 0 }}>
                    Please log out and log back in to see your updated balance.
                  </p>
                </div>

                <button
                  onClick={() => setPurchaseResult(null)}
                  style={{
                    width: '100%',
                    backgroundColor: '#3b198e',
                    color: '#ffffff',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '500',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2d1270';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b198e';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  Close
                </button>
              </>
            ) : purchaseResult.type === 'insufficient' ? (
              <>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <AlertTriangle style={{ width: '64px', height: '64px', color: '#eab308', margin: '0 auto 16px' }} />
                  <h2 style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                    Insufficient Balance
                  </h2>
                  <p style={{ color: '#9ca3af', fontSize: '14px' }}>
                    You don't have enough balance to complete this purchase.
                  </p>
                </div>

                <div style={{
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>Required:</span>
                      <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>
                        ${parseFloat(purchaseResult.required).toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>Available:</span>
                      <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>
                        ${parseFloat(purchaseResult.available).toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>Shortfall:</span>
                      <span style={{ color: '#ef4444', fontSize: '14px', fontWeight: '600' }}>
                        ${parseFloat(purchaseResult.shortfall).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => setPurchaseResult(null)}
                    style={{
                      flex: 1,
                      backgroundColor: '#374151',
                      color: '#ffffff',
                      padding: '12px',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: '500',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#4b5563';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#374151';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setPurchaseResult(null);
                      setShowTopupModal(true);
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: '#3b198e',
                      color: '#ffffff',
                      padding: '12px',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: '500',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#2d1270';
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#3b198e';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    Top Up Balance
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <AlertTriangle style={{ width: '64px', height: '64px', color: '#ef4444', margin: '0 auto 16px' }} />
                  <h2 style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                    Purchase Failed
                  </h2>
                  <p style={{ color: '#9ca3af', fontSize: '14px' }}>
                    {purchaseResult.message}
                  </p>
                </div>

                <button
                  onClick={() => setPurchaseResult(null)}
                  style={{
                    width: '100%',
                    backgroundColor: '#3b198e',
                    color: '#ffffff',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '500',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2d1270';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b198e';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ProductsWithErrorBoundary = () => (
  <ErrorBoundary>
    <Products />
  </ErrorBoundary>
);

export default ProductsWithErrorBoundary;
